import { Box, FormControlLabel } from '@mui/material';
import {
  GridColumnVisibilityModel,
  GridColumnsManagement,
  GridColumnsManagementProps,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from '@mui/x-data-grid-pro';

export type NbColumnsManagementProps = GridColumnsManagementProps & {
  /** Visibility of every column by default: what Reset gives back. */
  defaultModel: GridColumnVisibilityModel;
};

/**
 * MUI's columns panel, with its own footer: MUI's Reset goes back to the columns shown when the panel
 * opened, this one to the grid's default columns, and is enabled as soon as the columns differ from them.
 */
export default function NbColumnsManagement({ defaultModel, ...props }: NbColumnsManagementProps) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const model = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  const togglable = props.getTogglableColumns?.(columns);
  const hideable = columns.filter((column) => column.hideable !== false && (!togglable || togglable.includes(column.field)));
  const isVisible = (visibility: GridColumnVisibilityModel, field: string) => visibility[field] !== false;
  const allVisible = hideable.every((column) => isVisible(model, column.field));
  const allHidden = hideable.every((column) => !isVisible(model, column.field));
  const isDefault = columns.every((column) => isVisible(model, column.field) === isVisible(defaultModel, column.field));

  // Every key is written, `true` included: a deleted key would fall back to the grid's own default.
  const showAll = (visible: boolean) => {
    const next: GridColumnVisibilityModel = { ...model };
    columns.forEach((column) => {
      next[column.field] = hideable.includes(column) ? visible : isVisible(model, column.field);
    });
    apiRef.current.setColumnVisibilityModel(next);
  };

  return (
    <>
      <GridColumnsManagement {...props} disableShowHideToggle disableResetButton />
      {columns.length > 0 && (
        // Same layout as MUI's footer (MuiDataGrid-columnsManagementFooter)
        <Box
          className="MuiDataGrid-columnsManagementFooter"
          sx={{ py: 0.5, pr: 1, pl: 3, display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider' }}
        >
          <FormControlLabel
            control={
              <rootProps.slots.baseCheckbox
                disabled={hideable.length === 0}
                checked={allVisible}
                indeterminate={!allVisible && !allHidden}
                onClick={() => showAll(!allVisible)}
                name={apiRef.current.getLocaleText('columnsManagementShowHideAllText')}
                sx={{ p: 0.5 }}
                {...rootProps.slotProps?.baseCheckbox}
              />
            }
            label={apiRef.current.getLocaleText('columnsManagementShowHideAllText')}
          />
          <rootProps.slots.baseButton
            onClick={() => apiRef.current.setColumnVisibilityModel({ ...defaultModel })}
            disabled={isDefault}
            {...rootProps.slotProps?.baseButton}
          >
            {apiRef.current.getLocaleText('columnsManagementReset')}
          </rootProps.slots.baseButton>
        </Box>
      )}
    </>
  );
}
